/**
 * Sidebar Component - Modern professional navigation
 * Refactored with domain-based organization and section labels.
 * State: unified accordion Set persisted in localStorage.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  MapPinIcon,
  ServerStackIcon,
  CubeIcon,
  SwatchIcon,
  GlobeAltIcon,
  ComputerDesktopIcon,
  RectangleStackIcon,
  LockClosedIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  WifiIcon,
  ArrowsRightLeftIcon,
  BoltIcon,
  MapIcon,
  CircleStackIcon,
  CloudArrowDownIcon,
  KeyIcon,
  TableCellsIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  PrinterIcon,
  PhoneIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  SignalIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
  BuildingOfficeIcon,
  StarIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  VideoCameraIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  DOMAIN_GROUPS,
  MENU_SECTIONS,  PLATFORM_ADMIN_ITEM,
  findMenuItem,
  findGroupForMenuKey,
  findSectionForMenuKey,
  resolveMenuKeyForPath,
  resolveContextMenuForPath,
} from '../menuConfig';
import './Sidebar.css';

const DEFAULT_OPEN = new Set(['physical']);

/** Icono de cada sección en el menú compacto. */
const SECTION_ICONS = {
  infrastructure: ServerStackIcon,
  connectivity: GlobeAltIcon,
  virtualization: CpuChipIcon,
  services: WrenchScrewdriverIcon,
  management: Cog6ToothIcon,
};

const Sidebar = ({
  isOpen,
  onToggle,
  currentCompany,
  hiddenKeys = [],
  onOpenMenuCustomizer,
  isSuperadmin = false,
  menuMode = 'sections',
  favorites = { global: [], company: [] },
  onRemoveFavorite,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isSections = menuMode === 'sections';

  // Qué entradas del menú no debe ver este usuario en la empresa activa
  // (unión de la política de la empresa y su propia preferencia).
  const hidden = useMemo(
    () => (hiddenKeys instanceof Set ? hiddenKeys : new Set(hiddenKeys || [])),
    [hiddenKeys]
  );
  const isVisible = useCallback((key) => !hidden.has(key), [hidden]);

  const withFallback = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  // Unified accordion state: Set of open group keys
  const [openAccordions, setOpenAccordions] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_open_accordions_v2');
      return saved ? new Set(JSON.parse(saved)) : DEFAULT_OPEN;
    } catch {
      return DEFAULT_OPEN;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('sidebar_open_accordions_v2', JSON.stringify([...openAccordions]));
    } catch {}
  }, [openAccordions]);

  const toggleAccordion = useCallback((key) => {
    setOpenAccordions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const collapseAllAccordions = useCallback(() => {
    setOpenAccordions(new Set());
  }, []);

  const expandAllAccordions = useCallback(() => {
    setOpenAccordions(new Set(DOMAIN_GROUPS.map((g) => g.key)));
  }, []);

  const isAccordionOpen = (key) => openAccordions.has(key);

  // ── Modo compacto: qué sección se está viendo ────────────────────────────
  // Se deduce de la ruta, no del último clic: un favorito, un resultado del
  // buscador o el historial tienen que aterrizar con su sección ya abierta.
  const routeMenuKey = useMemo(
    () => resolveMenuKeyForPath(location?.pathname),
    [location?.pathname]
  );
  const routeSection = useMemo(
    () => findSectionForMenuKey(routeMenuKey),
    [routeMenuKey]
  );

  const [activeSection, setActiveSection] = useState(() => {
    try {
      return localStorage.getItem('cmdb_sidebar_section') || null;
    } catch {
      return null;
    }
  });

  /* ── Menú de módulo (contextual) ──────────────────────────────────────────
     Al entrar en Estaciones de trabajo el menú pasa a ser el del módulo. El
     usuario puede salir con «Todas las secciones» o con la sección padre; ese
     descarte es estado local, así que no se queda pegado al navegar. */
  const contextMenu = useMemo(
    () => resolveContextMenuForPath(location?.pathname),
    [location?.pathname]
  );
  const [contextDismissed, setContextDismissed] = useState(false);

  useEffect(() => {
    setContextDismissed(false);
  }, [contextMenu?.key]);

  const showContextMenu = Boolean(contextMenu) && !contextDismissed;

  useEffect(() => {
    if (!isSections) return;
    if (!routeSection) return;
    setActiveSection(routeSection.key);
    try {
      localStorage.setItem('cmdb_sidebar_section', routeSection.key);
    } catch {}
    // Al entrar en una entrada de la sección, su grupo se abre solo: si no,
    // el usuario vería el título del grupo cerrado y parecería que no hay nada.
    const group = findGroupForMenuKey(routeMenuKey);
    if (group) {
      setOpenAccordions((prev) => (prev.has(group.key) ? prev : new Set(prev).add(group.key)));
    }
  }, [isSections, routeSection, routeMenuKey]);

  // Si la sección guardada en localStorage ya no existe (menú reordenado) o se
  // queda vacía para este usuario, se vuelve a la raíz en vez de pintar nada.
  const openSection = useCallback((key) => {
    setActiveSection(key);
    try {
      if (key) localStorage.setItem('cmdb_sidebar_section', key);
      else localStorage.removeItem('cmdb_sidebar_section');
    } catch {}
  }, []);


  // Icon resolution map
  const ICON_MAP = {
    dashboard:            ChartBarIcon,
    locations:            MapPinIcon,
    racks:                RectangleStackIcon,
    servers:              ServerStackIcon,
    chassis:              ServerStackIcon,
    storage:              CircleStackIcon,
    switches:             ArrowsRightLeftIcon,
    routers:              GlobeAltIcon,
    firewalls:            ShieldCheckIcon,
    pdus:                 BoltIcon,
    ups:                  BoltIcon,
    'patch-panels':       TableCellsIcon,
    'access-points':      ShieldCheckIcon,
    cabling:              LinkIcon,
    // Submenú «Cableado»: mismas vistas que antes eran pestañas de una página.
    'cabling-designer':   ArrowsRightLeftIcon,
    'cabling-wiring':     ArrowsRightLeftIcon,
    'cabling-map':        MapIcon,
    'cabling-outlets':    BuildingOfficeIcon,
    'cabling-canvas':     LinkIcon,
    workstations:         ComputerDesktopIcon,
    // El menú usaba el icono genérico (CubeIcon) y la pantalla de planes el
    // ClipboardDocumentCheckIcon: no coincidían.
    'work-plans':         ClipboardDocumentCheckIcon,
    'company-docs':       DocumentTextIcon,
    'ip-phones':          PhoneIcon,
    printers:             PrinterIcon,
    peripherals:          PuzzlePieceIcon,
    'networks-dashboard': ChartBarIcon,
    'networks-blocks':    RectangleStackIcon,
    'networks-subnets':   Squares2X2Icon,
    'networks-explorer':  MagnifyingGlassIcon,
    'networks-assign':    LinkIcon,
    'networks-vlans':     TableCellsIcon,
    'networks-ips':       ServerStackIcon,
    'networks-vips':      ShieldCheckIcon,
    'networks-dhcp':      BoltIcon,
    'virt-overview':      ChartBarIcon,
    'virt-topology':      MapIcon,
    'virt-hypervisors':   CpuChipIcon,
    'virt-clusters':      Squares2X2Icon,
    'virt-datastores':    RectangleStackIcon,
    'virt-vswitches':     SwatchIcon,
    'virt-vms':           ComputerDesktopIcon,
    'wifi-dashboard':     WifiIcon,
    'wifi-topology':      MapIcon,
    'wifi-map':           MapPinIcon,
    'wifi-controllers':   WifiIcon,
    'wifi-aps':           WifiIcon,
    'wifi-radius':        LockClosedIcon,
    'wan-operators':      ArrowsRightLeftIcon,
    'wan-circuits':       LinkIcon,
    'wan-routers':        GlobeAltIcon,
    'service-farms':      ServerStackIcon,
    'backup-policies':    CloudArrowDownIcon,
    'db-instances':       CircleStackIcon,
    'licenses':           KeyIcon,
    'web-sites':          GlobeAltIcon,
    'feedback-hub':       DocumentTextIcon,
    'power-canvas':       BoltIcon,
    'network-canvas':        CpuChipIcon,
    'network-topology':      MapIcon,
    'network-discovered':    SignalIcon,
    'connections-matrix':    TableCellsIcon,
    'datacenter-diagram': MapIcon,
    'services-designer':  WrenchScrewdriverIcon,
    'services-list':      WrenchScrewdriverIcon,
    'services-graph':     MapIcon,
    'services-depmap':    MapIcon,
    contacts:             UserGroupIcon,
    'cameras-topology':   VideoCameraIcon,
    'cameras-map':        VideoCameraIcon,
    'cameras-recorders':  VideoCameraIcon,
    'cameras-list':       VideoCameraIcon,
    'dns-domains':        GlobeAltIcon,
    certificates:         ShieldCheckIcon,
    'dns-providers':      GlobeAltIcon,
    vulnerabilities:      ShieldCheckIcon,
    'device-catalog':     CpuChipIcon,
    tutoriales:           BookOpenIcon,
    academia:             BookOpenIcon,
    roles:                ShieldCheckIcon,
    naming:               DocumentTextIcon,
    'company-docs':       DocumentTextIcon,
    setup:                Cog6ToothIcon,
    'manual-cmdb':        BookOpenIcon,
    'agent-settings':     CpuChipIcon,
    'agent-mapping':      MapIcon,
  };

  const renderIcon = (key) => {
    const Icon = ICON_MAP[key] || CubeIcon;
    return <Icon className="sidebar-icon" />;
  };

  /** Enlace de una entrada dentro de un grupo (segundo nivel). */
  const renderItemLink = (child) => (
    <NavLink
      key={child.key}
      to={child.path}
      end={child.path === '/networks' || child.path === '/services'}
      className={({ isActive }) =>
        `sidebar-link sidebar-sublink ${isActive ? 'active' : ''} ${!child.implemented ? 'not-implemented' : ''}`
      }
      title={!child.implemented ? t('menu.underConstruction', '🚧 En construcción') : ''}
    >
      {renderIcon(child.key)}
      <span className="sidebar-label">{t(`menu.items.${child.key}`, child.label)}</span>
      {!child.implemented && <span className="sidebar-badge">🚧</span>}
    </NavLink>
  );

  /**
   * Renders one domain group as an accordion (only the items it receives).
   *
   * Con `flat` se pintan solo las entradas, sin la cabecera del grupo: es el
   * caso de las secciones que tienen un único grupo (Virtualización, Servicios,
   * Gestión), donde repetir el título de la sección con el del grupo debajo
   * era ruido puro.
   */
  const renderGroup = (group, items, { flat = false } = {}) => {
    if (flat) {
      return (
        <div key={`flat-${group.key}`} className="sidebar-section-flat">
          {items.map(renderItemLink)}
        </div>
      );
    }
    const open = isAccordionOpen(group.key);
    const groupLabel = t(`menu.groups.${group.key}`, group.label);
    return (
      <div key={group.key} className="sidebar-accordion">
        <button
          className={`sidebar-accordion-toggle ${open ? 'active' : ''}`}
          onClick={() => toggleAccordion(group.key)}
          aria-expanded={open}
          title={groupLabel}
        >
          <span className="sidebar-group-dot" style={{ background: group.color }} />
          <span className="sidebar-label">{groupLabel}</span>
          <span className="sidebar-arrow">
            {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </button>
        {open && (
          <div className="sidebar-accordion-content">{items.map(renderItemLink)}</div>
        )}
      </div>
    );
  };

  const domainByKey = useMemo(
    () => Object.fromEntries(DOMAIN_GROUPS.map(g => [g.key, g])),
    []
  );

  /** Enlace suelto del menú (Dashboard, Ubicaciones, Plataforma...) */
  const renderLink = (item) => (
    <NavLink
      key={item.key}
      to={item.path}
      end={item.path === '/networks' || item.path === '/services'}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? 'active' : ''} ${!item.implemented ? 'not-implemented' : ''}`
      }
      title={!item.implemented ? t('menu.underConstruction', '🚧 En construcción') : ''}
    >
      {renderIcon(item.key)}
      <span className="sidebar-label">{t(`menu.items.${item.key}`, item.label)}</span>
      {!item.implemented && <span className="sidebar-badge">🚧</span>}
    </NavLink>
  );

  /**
   * Secciones y entradas que realmente pinta el menú: se descartan las entradas
   * ocultas y las secciones (y grupos) que se quedan sin nada que mostrar.
   */
  const sections = useMemo(
    () =>
      MENU_SECTIONS.map((section) => ({
        key: section.key,
        label: section.label,
        entries: section.entries
          .map((entry) => {
            if (entry.type === 'link') {
              const item = findMenuItem(entry.key);
              return item && isVisible(item.key) ? { type: 'link', item } : null;
            }
            const group = domainByKey[entry.key];
            if (!group) return null;
            const baseItems = isSuperadmin
              ? [...group.items, ...(group.key === 'admin' ? [PLATFORM_ADMIN_ITEM] : [])]
              : group.items;
            const items = baseItems
              .filter((item) => !(entry.skipItems || []).includes(item.key))
              // Entradas que viven dentro de un módulo (`menuHidden`): se
              // llega a ellas desde el menú del módulo, no sueltas aquí.
              .filter((item) => !item.menuHidden)
              // Entradas marcadas `superadminOnly` (p. ej. Proveedores DNS):
              // guardan credenciales de plataforma y no se enseñan a clientes.
              .filter((item) => !item.superadminOnly || isSuperadmin)
              .filter((item) => isVisible(item.key));
            return items.length ? { type: 'group', group, items } : null;
          })
          .filter(Boolean),
      })).filter((section) => section.entries.length > 0),
    [domainByKey, isVisible, isSuperadmin]
  );

  // En el modo compacto, los enlaces sueltos (Dashboard, Documentación) van
  // siempre a la vista y las secciones son los botones de primer nivel.
  const standaloneLinks = useMemo(() => {
    const general = sections.find((section) => section.key === 'general');
    return (general?.entries || []).filter((entry) => entry.type === 'link').map((entry) => entry.item);
  }, [sections]);

  const sectionButtons = useMemo(
    () => sections.filter((section) => section.key !== 'general' && section.label),
    [sections]
  );

  /**
   * Accesos que NO se pierden al entrar en un módulo: los generales (Dashboard,
   * Planes, Documentación) y los fijos de infraestructura (Ubicaciones,
   * Contactos). El módulo estrecha el menú, no lo encierra.
   */
  const contextFixedLinks = useMemo(() => {
    const keys = ['dashboard', 'work-plans', 'company-docs', 'locations', 'contacts'];
    // Se descarta lo que el módulo ya ofrece con el mismo nombre: en Estaciones
    // de trabajo su «Dashboard» y sus «Planes» ya están arriba y repetirlos con
    // otro destino sería una trampa.
    const yaEnElModulo = new Set(
      (contextMenu?.items || []).map((item) => item.label.toLowerCase()),
    );
    return keys
      .map((key) => findMenuItem(key))
      .filter((item) => item && isVisible(item.key) && !yaEnElModulo.has(item.label.toLowerCase()));
  }, [isVisible, contextMenu]);

  const activeSectionData = useMemo(
    () => sectionButtons.find((section) => section.key === activeSection) || null,
    [sectionButtons, activeSection]
  );

  // Sección con un único grupo y sin enlaces sueltos: se pinta plano, sin
  // repetir el nombre del grupo bajo el título de la sección.
  const singleGroupEntry = useMemo(() => {
    if (!activeSectionData || activeSectionData.entries.length !== 1) return null;
    const [entry] = activeSectionData.entries;
    return entry.type === 'group' ? entry : null;
  }, [activeSectionData]);

  // Favoritos visibles: se deduplica (un favorito global y uno de empresa
  // pueden apuntar a lo mismo) y se descarta lo que la empresa oculta.
  const visibleFavorites = useMemo(() => {
    const all = [...(favorites?.global || []), ...(favorites?.company || [])];
    const seen = new Set();
    return all.filter((favorite) => {
      if (!favorite?.menu_key || seen.has(favorite.menu_key)) return false;
      if (!isVisible(favorite.menu_key) || !findMenuItem(favorite.menu_key)) return false;
      seen.add(favorite.menu_key);
      return true;
    });
  }, [favorites, isVisible]);

  const renderSectionIcon = (key) => {
    const Icon = SECTION_ICONS[key] || CubeIcon;
    return <Icon className="sidebar-icon" />;
  };

  const renderFavorite = (favorite) => {
    const item = findMenuItem(favorite.menu_key);
    if (!item) return null;
    const label = favorite.label || t(`menu.items.${item.key}`, item.label);
    return (
      <div key={`fav-${favorite.menu_key}`} className="sidebar-favorite">
        <NavLink
          to={favorite.path || item.path}
          className={({ isActive }) =>
            `sidebar-link sidebar-sublink sidebar-favorite-link ${isActive ? 'active' : ''}`
          }
          title={label}
        >
          <StarIcon className="sidebar-icon sidebar-favorite-star" />
          <span className="sidebar-label">{label}</span>
          {favorite.scope === 'global' && (
            <span
              className="sidebar-favorite-scope"
              title={t('menu.favorites.globalHint', 'Visible en todas tus empresas')}
            >
              <GlobeAltIcon />
            </span>
          )}
        </NavLink>
        {onRemoveFavorite && (
          <button
            type="button"
            className="sidebar-favorite-remove"
            onClick={() => onRemoveFavorite(favorite)}
            title={t('menu.favorites.remove', 'Quitar de favoritos')}
            aria-label={t('menu.favorites.remove', 'Quitar de favoritos')}
          >
            <XMarkIcon />
          </button>
        )}
      </div>
    );
  };

  /** «Todas las secciones»: sale del módulo y vuelve a la raíz del menú. */
  const goToSections = () => {
    setContextDismissed(true);
    if (isSections) openSection(null);
  };

  /** «Infraestructura»: sale del módulo y abre la sección padre. */
  const goToParentSection = () => {
    setContextDismissed(true);
    const parentKey = contextMenu?.parentSectionKey;
    if (!parentKey) return;
    if (isSections) {
      openSection(parentKey);
      return;
    }
    // Menú clásico: no hay «sección activa», así que se despliegan sus grupos.
    const parent = MENU_SECTIONS.find((section) => section.key === parentKey);
    const groupKeys = (parent?.entries || [])
      .filter((entry) => entry.type === 'group')
      .map((entry) => entry.key);
    setOpenAccordions((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((key) => next.add(key));
      return next;
    });
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && <div className="sidebar-overlay visible" onClick={onToggle} />}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* La marca va FUERA del área con scroll: así su borde inferior llega de
            lado a lado (sin el hueco del scrollbar) y cuadra con la barra superior. */}
        <div className="sidebar-brand" onClick={() => window.location.assign('/dashboard')} role="button" tabIndex={0}>
          <div className="sidebar-brand-icon">L6</div>
          <div className="sidebar-brand-texts">
            <span className="sidebar-brand-title">CMDB</span>
            <span className="sidebar-brand-subtitle">Infrastructure</span>
          </div>
        </div>

        <div className="sidebar-content">

          {/* Company Info */}
          {currentCompany && (
            <div className="sidebar-company">
              <span className="sidebar-company-label">{withFallback('company.current', 'Empresa')}</span>
              <span className="sidebar-company-name">{currentCompany.label}</span>
            </div>
          )}

          {/* Navigation */}
          <nav className="sidebar-nav">
            {showContextMenu ? (
              <div className="sidebar-section-panel sidebar-context-panel">
                <button type="button" className="sidebar-back-btn" onClick={goToSections}>
                  <ChevronLeftIcon className="sidebar-back-icon" />
                  <span>{t('menu.sectionsBack', 'Todas las secciones')}</span>
                </button>
                <button
                  type="button"
                  className="sidebar-back-btn sidebar-back-btn--section"
                  onClick={goToParentSection}
                >
                  <ChevronLeftIcon className="sidebar-back-icon" />
                  <span>
                    {t(
                      `menu.sections.${contextMenu.parentSectionKey}`,
                      contextMenu.parentSectionLabel,
                    )}
                  </span>
                </button>
                <div className="sidebar-section-title">
                  {renderIcon(contextMenu.icon || contextMenu.key)}
                  {contextMenu.label}
                </div>
                {contextMenu.items.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    end={Boolean(item.end)}
                    className={({ isActive }) => `sidebar-link sidebar-sublink ${isActive ? 'active' : ''}`}
                  >
                    <span className="sidebar-label">
                      {t(`menu.context.${item.key}`, item.label)}
                    </span>
                  </NavLink>
                ))}
                {contextFixedLinks.length > 0 && (
                  <div className="sidebar-context-fixed">
                    <div className="sidebar-section-label">
                      {t('menu.context.fixed', 'SIEMPRE A MANO')}
                    </div>
                    {contextFixedLinks.map(renderLink)}
                  </div>
                )}
              </div>
            ) : isSections ? (
              <>
                {/* Favoritos: accesos directos del usuario, por encima de las
                    secciones. Se oculta el bloque entero si no queda ninguno
                    visible para esta empresa. */}
                {visibleFavorites.length > 0 && (
                  <div className="sidebar-favorites">
                    <div className="sidebar-section-label">
                      {t('menu.favorites.title', 'FAVORITOS')}
                    </div>
                    {visibleFavorites.map(renderFavorite)}
                  </div>
                )}

                {standaloneLinks.length > 0 && (
                  <div className="sidebar-fixed-links">
                    {standaloneLinks.map(renderLink)}
                  </div>
                )}

                {activeSectionData ? (
                  <div className="sidebar-section-panel">
                    <button
                      type="button"
                      className="sidebar-back-btn"
                      onClick={() => openSection(null)}
                    >
                      <ChevronLeftIcon className="sidebar-back-icon" />
                      <span>{t('menu.sectionsBack', 'Todas las secciones')}</span>
                    </button>
                    <div className="sidebar-section-title">
                      {t(`menu.sections.${activeSectionData.key}`, activeSectionData.label)}
                    </div>
                    {singleGroupEntry
                      ? renderGroup(singleGroupEntry.group, singleGroupEntry.items, { flat: true })
                      : activeSectionData.entries.map((entry) =>
                          entry.type === 'link'
                            ? renderLink(entry.item)
                            : renderGroup(entry.group, entry.items)
                        )}
                    {currentCompany && onOpenMenuCustomizer && (
                      <button
                        type="button"
                        className="sidebar-link sidebar-sublink sidebar-customize-link"
                        onClick={() => onOpenMenuCustomizer('favorites')}
                        title={t('menu.favorites.editHint', 'Elige tus accesos directos')}
                      >
                        <StarIcon className="sidebar-icon" />
                        <span className="sidebar-label">
                          {t('menu.favorites.edit', 'Editar favoritos')}
                        </span>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="sidebar-sections">
                    {sectionButtons.map((section) => {
                      const isCurrent = routeSection?.key === section.key;
                      return (
                        <button
                          key={section.key}
                          type="button"
                          className={`sidebar-link sidebar-section-link ${isCurrent ? 'active' : ''}`}
                          onClick={() => openSection(section.key)}
                          aria-current={isCurrent ? 'true' : undefined}
                        >
                          {renderSectionIcon(section.key)}
                          <span className="sidebar-label">
                            {t(`menu.sections.${section.key}`, section.label)}
                          </span>
                          <ChevronRightIcon className="sidebar-section-arrow" />
                        </button>
                      );
                    })}
                    {currentCompany && onOpenMenuCustomizer && (
                      <button
                        type="button"
                        className="sidebar-link sidebar-sublink sidebar-customize-link"
                        onClick={() => onOpenMenuCustomizer('favorites')}
                        title={t('menu.favorites.editHint', 'Elige tus accesos directos')}
                      >
                        <StarIcon className="sidebar-icon" />
                        <span className="sidebar-label">
                          {visibleFavorites.length
                            ? t('menu.favorites.edit', 'Editar favoritos')
                            : t('menu.favorites.add', 'Añadir favoritos')}
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              sections.map((section, index) => (
                <React.Fragment key={section.key || `section-${index}`}>
                  {section.label && (
                    <div className="sidebar-section-label">
                      {t(`menu.sections.${section.key}`, section.label)}
                    </div>
                  )}
                  {section.entries.map((entry) =>
                    entry.type === 'link'
                      ? renderLink(entry.item)
                      : renderGroup(entry.group, entry.items)
                  )}
                  {/* Acceso al personalizador: con etiqueta, porque la barra de
                      controles de abajo solo muestra iconos. */}
                  {section.key === 'management' && currentCompany && onOpenMenuCustomizer && (
                    <button
                      type="button"
                      className="sidebar-link sidebar-sublink"
                      onClick={() => onOpenMenuCustomizer()}
                      title={t(
                        'menu.customize.hint',
                        'Elige qué entradas del menú quieres ver en esta empresa'
                      )}
                    >
                      <Squares2X2Icon className="sidebar-icon" />
                      <span className="sidebar-label">{t('menu.customize.title', 'Personalizar menú')}</span>
                    </button>
                  )}
                </React.Fragment>
              ))
            )}
          </nav>

          <div className="sidebar-controls">
            {onOpenMenuCustomizer && (
              <button
                type="button"
                className="sidebar-control-btn"
                onClick={() => onOpenMenuCustomizer()}
                title={t(
                  'menu.customize.hint',
                  'Elegir qué entradas del menú quieres ver en esta empresa'
                )}
                aria-label={t('menu.customize.title', 'Personalizar menú')}
              >
                <Squares2X2Icon className="sidebar-control-icon" />
                <span className="sidebar-label">{t('menu.customize.title', 'Personalizar menú')}</span>
              </button>
            )}
            {/* Plegar/desplegar solo tiene sentido con acordeones a la vista:
                en el modo compacto, dentro de una sección; en el clásico, siempre. */}
            {(isSections ? !!activeSectionData : true) && (
              <>
                <button
                  type="button"
                  className="sidebar-control-btn"
                  onClick={collapseAllAccordions}
                  title="Plegar todas las secciones"
                  aria-label="Plegar todas las secciones"
                >
                  <ChevronDoubleUpIcon className="sidebar-control-icon" />
                  <span className="sidebar-label">Plegar todo</span>
                </button>
                <button
                  type="button"
                  className="sidebar-control-btn"
                  onClick={expandAllAccordions}
                  title="Desplegar todas las secciones"
                  aria-label="Desplegar todas las secciones"
                >
                  <ChevronDoubleDownIcon className="sidebar-control-icon" />
                  <span className="sidebar-label">Desplegar todo</span>
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
